import {React, observer, useLocalStore, classNames, useAsObservableSource, Suspense, useEffect, useLayoutEffect, Fragment, trace, computed} from '@api'
import {TabNode} from '@flexlayout'
import {CatalogPdfExporterContainer} from '@lazy'
import {PdfSidebar} from '@lazy'
import {icons, IFlexLayoutTabProps, site} from '@stores'
import {AssortmentRoot, CatalogDataGridRoot, CatalogFooter, CatalogItemsPanel, CatalogReportView, CatalogRoot, CatalogSeasonSelectors, CatalogToolbarItems, CollectionTargetsToolbar, CreateSlider, CurrentWorkspaceSyncToolbarButton, DropTarget, ErrorBoundary, FilterChipsToolbar, GroupByPivot, GroupByToolbar, InlineAggregateDetails, LoadingIndicator, RightSidebar, RightSidebarBody, RootGroups, SeasonSelectorBreadcrumbs, SeasonSelectorNavigation, SelectAssortmentToolbarButton, StoresInstanceContext, useCatalogRootStore, useStores} from '@modules'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './CatalogRootFlexLayoutTab.css'
import {CatalogRootFlexLayoutStore, CatalogRootFlexLayoutStoreContext, useCatalogRootFlexLayoutStore} from '.'
import {PDFServerRenderedContainer} from '@modules/pdf'

/// region Components

interface MyProps extends IFlexLayoutTabProps {
  className?: string
}

/**
 * The root of some specific catalog view
 *
 * Right now we're relegated to global state for stuff like vlp hierarchy, filters, grouping, etc
 *
 * For #1 I want to be able to target different viewModes in a given tab - card / table / pdf
 */
export const CatalogRootFlexLayoutTab = observer<MyProps>(function CatalogRootFlexLayoutTab({className, flexNode}) {
  const tab = flexNode as TabNode,
    {flexLayout, catalogRoot: store} = tab,
    {hideToolbars, flexLayout: {currentReport}, node, everVisible, workspaces, onContextMenu} = (store ?? {}) as Partial<CatalogRootFlexLayoutStore>
  // {mounted, rendered} = useDelayedRender(true, {
  //   enterDelay: 50,
  //   exitDelay: 50
  // })

  // console.log(`Render ${id} - ${node.name} - ${catalog.workspace.id} - ${store.config.workspace} - ${workspace}`, {mounted, rendered})
  // useEffect(() => {
  //   console.log(`Mount ${tab.id} ${tab?.name}`, {flexNode})
  //   return () => console.log(`Unmount ${tab?.id} ${tab?.name}`, {flexNode})
  // }, [])

  // useLayoutEffect(() => {
  //   if (store instanceof CatalogRootFlexLayoutStore) {
  //     store.hasRendered = rendered
  //   }
  // }, [rendered, store.hasRendered])

  // console.log(`Render RootCatalogViewFlexLayout`, {everVisible, visible, mounted, rendered, store, workspaces, id: store?.stores?.storeId})

  // if (isForDifferentDeployment) {
  //   return <div className={css.invalidCatalog}>
  //     <span>This tab belongs to the deployment '{config.deployment}'<br/>You are in the deployment '{selectedDeploymentId}'<br/>This should only happen in dev mode.</span>
  //   </div>
  // }

  if (!store?.storesToInject) {
    return null
  }

  const {setScrollPanel, storesToInject: stores, stickyToolbars, storesToInject: {catalog, catalog: {assortments}}} = store

  // console.log(stores._id, store, workspaces, hasRendered)
  return (
    <CatalogRootFlexLayoutStoreContext.Provider value={store as CatalogRootFlexLayoutStore}>
      <StoresInstanceContext.Provider value={stores}>
        {workspaces ?
         <Fragment>
           <div key={workspaces?.current}
                data-id={node?.id}
                data-type='CatalogRootContent'
                data-variant={assortments?.selectedAssortment ? 'assortment' : catalog.settings.viewMode}
                data-sidebar-type=''
                data-workspace={workspaces?.current}
                data-sticky-toolbars={stickyToolbars ? true : undefined}
                data-hide-toolbars={stickyToolbars && hideToolbars && !site.activePopover && !site.isShowingPopover ? true : undefined}
                onContextMenu={onContextMenu}
                onMouseEnter={() => flexLayout.hoveredTabSet = tab.tabSet.id}
                onMouseLeave={() => {
                  if (flexLayout.hoveredTabSet == tab.tabSet.id) {
                    flexLayout.hoveredTabSet = null
                  }
                }}
                className={classNames(css.root, className)}
             // onScroll={onScroll}
           >
             <ErrorBoundary>
               {!everVisible && !currentReport?.aboutToSubmitPdfJob ? null :
                <Suspense fallback={<LoadingIndicator className={css.loading}/>}>
                  <CatalogRootContent/>
                </Suspense>}
             </ErrorBoundary>
           </div>
         </Fragment> : <div>No Workspaces...</div>}
      </StoresInstanceContext.Provider>
    </CatalogRootFlexLayoutStoreContext.Provider>)
})

export const CatalogRootContent = observer(function CatalogRootContent() {
  const rootStore = useCatalogRootFlexLayoutStore(),
    {catalog, setScrollPanel, stores: {flexLayout, currentUser: {focus_hideToolbars}}, onScroll, stores, showDropTarget} = rootStore,
    {settings: {showAggs}} = catalog
  return (
    <Fragment>
      <Suspense fallback={null}>
        <div
          className={css.stickyContainer}
          ref={setScrollPanel}
          onScroll={onScroll}
          data-hide-toolbars={focus_hideToolbars}
        >
          {catalog.initialized &&
		   <div className={css.toolbars}>
             {!flexLayout.showCatalogWidgetsInTopBar &&
			  <Fragment>
				  <SeasonSelectorToolbar/>
				  <div className={css.inlineToolbarItems}>
					  <div className={css.left}>
						  <GroupByToolbar/>
					  </div>
					  <div className={css.right}>
						  <CatalogToolbarItems/>
					  </div>
				  </div>
			  </Fragment>}
             {catalog.targetsToolbarConfig && catalog.settings.showTargetsToolbar && <CollectionTargetsToolbar className={css.collectionTargetsToolbar}/>}
			   <FilterChipsToolbar className={css.secondaryToolbar}/>
             {showAggs && catalog.isProductView && catalog.settings.aggregateLocation === 'inline' && <InlineAggregateDetails className={css.globalCatalogAggs}/>}
		   </div>}

          <div className={css.catalogContentsWrapper}>
            <CatalogRootFlexLayoutTabContents/>
            {/*<CatalogFlexLayoutTabModalDetails/>*/}
          </div>
        </div>
        {catalog.settings.toolbarItems.footerToolbar.visible === true && catalog.isDataTableView === false && <CatalogFooter className={css.footer}/>}
        {/*<RightSidebar />*/}
        <DropTarget
          stores={stores}
          visible={showDropTarget}
          title={catalog.catalogItemName}
          targetType='Catalog_CreateItems'
          className={css.dropTarget}
        />
        {/*<ProductCountPopOver/>*/}
        <CreateSlider/>
      </Suspense>
    </Fragment>)
})
/// endregion

/**
 * The part below the toolbars that contains the actual catalog component we're in (assortments/pdf/card view/table/pivot/???)
 */
export const CatalogRootFlexLayoutTabContents = observer(function CatalogRootFlexLayoutTabContents() {
  const store = useCatalogRootFlexLayoutStore()

  if (!store.catalogRoot) {
    return null
  }

  const {viewMode, catalogRoot: {stores: {sidebar, cloudPDF}}, props: {flexNode, flexNode: {isPreviewLayout}}, flexLayout: {maximizedTab}} = store
  switch (viewMode) {
    case 'pdf':
      return isPreviewLayout ? <LayoutPreviewPanel label='PDF'/>
                             : <ClickToLoadPanel bypass={maximizedTab === flexNode || DEBUG} message='PDF'>
               <div className={css.pdfWrapper}>
                 {cloudPDF?.enabled ? <PDFServerRenderedContainer/> : <CatalogPdfExporterContainer className={css.pdf}/>}
                 {flexNode === maximizedTab && sidebar?.right?.open && <PdfSidebar className={css.pdfSidebar}/>}
               </div>
             </ClickToLoadPanel>
    case  'report':
      return <CatalogReportView/>
    case  'table': {
      return (
        isPreviewLayout ? <LayoutPreviewPanel label='Catalog'/> :
        <CatalogRoot className={css.catalogRoot}>
          <CatalogDataGridRoot/>
        </CatalogRoot>)
    }
    case 'card': {
      const {stores: {catalog: {assortments: {selectedAssortment}, settings: {assortment: {groupingBehavior}}, isGroupedView, itemCount, queryVariablesEs, filteredOfflineItems}}} = store

      return (
        isPreviewLayout ? <LayoutPreviewPanel label='Catalog'/> :
        <CatalogRoot className={css.catalogRoot}>
          {
            isGroupedView && (!selectedAssortment || (selectedAssortment && groupingBehavior === 'outside')) ? <RootGroups className={css.groupsRoot}/>
                                                                                                             : selectedAssortment ? <AssortmentRoot/>
                                                                                                                                  : <CatalogItemsPanel
                                                                                                                 onlineItemCount={!_.isNumber(itemCount) ? null : itemCount - filteredOfflineItems.length}
                                                                                                                 offlineItemCount={filteredOfflineItems.length}
                                                                                                                 //itemsPerClick={5}
                                                                                                                 queryVariables={queryVariablesEs}
                                                                                                                 className={css.productsView}
                                                                                                               />
          }
        </CatalogRoot>)
    }

    default: {
      break
    }
  }

  return <div>Unknown view mode {viewMode}</div>
})

export const LayoutPreviewPanel = observer<{ label: string }>(function LayoutPreviewPanel({label}) {
  return (<div className={css.layoutPreviewPanel} data-type='PreviewPanel'>
    <span>{label}</span>
  </div>)
})

export const ClickToLoadPanel = observer<{ bypass?: boolean, message?: string }>(function ClickToLoadPanel({children, bypass, message}) {
  const store = useLocalStore(() => ({
      everClicked: false,
      markClicked: () => store.everClicked = true
    })),
    {everClicked, markClicked} = store

  return (<div data-loaded={everClicked} className={css.clickToLoadPanel} onClick={everClicked ? undefined : markClicked}>
    {everClicked || bypass || !DEBUG ? children : <span className={css.clickToLoad}>Click to Load{message ? ` ${message}` : ''}</span>}
  </div>)
})

const CatalogFlexLayoutTabModalDetails = observer(function CatalogFlexLayoutTabModalDetails() {
  const {stores: {sidebar: {right, right: {showDetailsView, type, path}}}} = useCatalogRootFlexLayoutStore()

  if (type != 'modal') return null

  return <div className={css.tabModalWrapper}
              onClick={(e) => {
                if (e.target === e.currentTarget && showDetailsView) {
                  right.open = false
                }
              }}
              data-open={showDetailsView}>
    <RightSidebar/>
  </div>
})

export const SeasonSelectorToolbar = observer(function SeasonSelectorToolbar() {
  const {catalog} = useStores()

  return (<Fragment>
    {!_.isEmpty(catalog.seasonSelectorFields) &&
	 <div className={css.navToolbar}>
		 <div className={css.left}>
			 <SeasonSelectorNavigation/>
			 <ClearHierarchyToolbarButton/>
           {/*<CurrentWorkspaceSyncToolbarButton className={css.syncWorkspace} location='toolbar' key='sync'/>*/}
		 </div>
		 <div className={css.right}>
			 <SelectAssortmentToolbarButton/>
			 <CatalogSeasonSelectors align='right'/>
		 </div>
	 </div>}
  </Fragment>)
})

export const ClearHierarchyToolbarButton = observer(function ClearHierarchyToolbarButton() {
  const {strings, catalog: {vlp, seasonSelector}} = useStores()

  if (!seasonSelector || !vlp?.hierarchy?.hasVlpHierarchy || !vlp?.hierarchy?.hasSelections) return null

  return (<PrimaryIconButton icon={icons.clear} showOnHover tooltip={`Clear`} tooltipPosition='bottom' onClick={seasonSelector?.clearBreadcrumbs}/>)
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/content-panels/CatalogRootFlexLayoutTab.tsx